/**
 * MentionInput — a controlled text input with @-triggered autocomplete
 * of org members. Sits between a plain <textarea> and a full rich-text
 * editor. Uses only lookahead/behind of caret + a small floating list.
 *
 * Contract:
 *   value             — current text, stored by the caller
 *   onChange(text, mentions)
 *                     — fires on every keystroke. `mentions` is the
 *                       current structured [{user_id, username}] array
 *                       that stays in sync with the `@<username>` tokens
 *                       actually present in `text` (see pruneMentions
 *                       below — if a user deletes a `@<name>` from the
 *                       text, the matching mention is dropped).
 *   placeholder, rows, testId, disabled — standard passthroughs.
 *
 * The list of org-mentionable users is fetched once via
 * GET /api/users/mentionable and cached per-mount.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios, { API } from '../../utils/api';

const TEAL = '#4DB6AC';

// Drop mentions whose display token no longer appears in the text —
// keeps the structured array honest when a user backspaces over the
// `@Name` sequence. Case-insensitive match on the token exactly as
// inserted ("@<Name> " with a trailing space, or "@<Name>" at end).
function pruneMentions(text, mentions) {
  if (!mentions?.length) return [];
  const lower = text.toLowerCase();
  return mentions.filter((m) => lower.includes(`@${m.username.toLowerCase()}`));
}

// Find whether the caret is currently inside an "@partial" prefix. If
// yes, return `{start, query}` where `start` is the position of the `@`
// so we know what range to replace on selection.
function activeMentionPrefix(text, caret) {
  // Walk backwards from the caret until whitespace or start-of-text.
  // If we hit '@' first, that's the prefix.
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '@') {
      // '@' only counts as a mention trigger at start-of-text or after
      // whitespace. Prevents email-address @ from opening the dropdown.
      if (i === 0 || /\s/.test(text[i - 1])) {
        return { start: i, query: text.slice(i + 1, caret) };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i--;
  }
  return null;
}

const MentionInput = ({
  value,
  onChange,
  placeholder = 'Write a comment…',
  rows = 3,
  testId = 'mention-input',
  disabled = false,
  mentions = [],
  style = {},
}) => {
  const textareaRef = useRef(null);
  const [allUsers, setAllUsers] = useState([]);
  const [dropdown, setDropdown] = useState(null); // { start, query, index }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/mentionable`);
        if (!cancelled) setAllUsers(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setAllUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!dropdown) return [];
    const q = dropdown.query.toLowerCase();
    // Empty query = show all (up to 8). Otherwise startswith first, then
    // substring — but keep the merged list unique + capped.
    const starts = allUsers.filter((u) => u.username.toLowerCase().startsWith(q));
    const contains = allUsers.filter((u) => !u.username.toLowerCase().startsWith(q) && u.username.toLowerCase().includes(q));
    return [...starts, ...contains].slice(0, 8);
  }, [dropdown, allUsers]);

  const emit = (text, updatedMentions) => {
    onChange(text, pruneMentions(text, updatedMentions));
  };

  const handleChange = (e) => {
    const text = e.target.value;
    const caret = e.target.selectionStart ?? text.length;
    emit(text, mentions);
    const active = activeMentionPrefix(text, caret);
    if (active && active.query.length <= 40) {
      setDropdown({ ...active, index: 0 });
    } else {
      setDropdown(null);
    }
  };

  const applySelection = (user) => {
    if (!dropdown || !textareaRef.current) return;
    const caret = textareaRef.current.selectionStart ?? value.length;
    const before = value.slice(0, dropdown.start);
    const after = value.slice(caret);
    const insertion = `@${user.username} `;
    const nextText = before + insertion + after;
    const nextMentions = [...mentions];
    if (!nextMentions.some((m) => m.user_id === user.id)) {
      nextMentions.push({ user_id: user.id, username: user.username });
    }
    emit(nextText, nextMentions);
    setDropdown(null);
    // Restore caret just after the inserted token, on the next tick.
    const nextCaret = (before + insertion).length;
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(nextCaret, nextCaret);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (!dropdown || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDropdown((d) => ({ ...d, index: (d.index + 1) % filtered.length }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDropdown((d) => ({ ...d, index: (d.index - 1 + filtered.length) % filtered.length }));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      applySelection(filtered[dropdown.index]);
    } else if (e.key === 'Escape') {
      setDropdown(null);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={textareaRef}
        data-testid={testId}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setDropdown(null), 100)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: 8,
          border: '1px solid #E5E7EB',
          fontSize: 14,
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
          fontFamily: 'inherit',
          ...style,
        }}
      />
      {dropdown && filtered.length > 0 && (
        <div
          data-testid="mention-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 200,
            overflowY: 'auto',
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            marginTop: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            zIndex: 20,
          }}
        >
          {filtered.map((u, i) => (
            <div
              key={u.id}
              data-testid={`mention-suggestion-${u.username}`}
              onMouseDown={(e) => { e.preventDefault(); applySelection(u); }}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 13,
                background: i === dropdown.index ? 'rgba(77, 182, 172, 0.08)' : 'transparent',
                color: '#1F2937',
                fontWeight: i === dropdown.index ? 600 : 400,
                borderBottom: i < filtered.length - 1 ? '1px solid #F3F4F6' : 'none',
              }}
            >
              <span style={{ color: TEAL, fontWeight: 600 }}>@</span>{u.username}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
